import React, { useState, useMemo } from 'react';
import { 
  Search, ChevronDown, HelpCircle, Sparkles, Globe, 
  Briefcase, MessageCircle, CheckCircle, ShieldCheck, 
  GraduationCap, Building2, Scale, CreditCard, FileText
} from 'lucide-react';
import { useTheme } from '../Home/ThemeContext';

export default function FAQPage() {
  const context = useTheme();
  const isDarkMode = context.isDarkMode || context.theme === 'dark';

  const [activeCategory, setActiveCategory] = useState('General');
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // --- STYLING CONSTANTS ---
  const styles = {
    bg: isDarkMode ? 'bg-[#030712]' : 'bg-gray-50',
    card: isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200',
    text: isDarkMode ? 'text-white' : 'text-gray-900',
    subText: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    link: 'text-blue-600 hover:text-blue-700 font-semibold hover:underline transition-colors',
    input: isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-900',
    activeTab: 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 border-transparent',
    inactiveTab: isDarkMode ? 'bg-gray-800/50 text-gray-400 border-gray-700' : 'bg-white text-gray-600 border-gray-200',
  };

  // --- RICH FAQ DATA ---
  const faqData = useMemo(() => ({
    "General": [
      {
        q: "What are the core benefits of being an agent on Vidyazon?",
        a: (
          <div className="space-y-4">
            <p>Vidyazon offers a structured, growth-focused partner model. Key benefits include:</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                "Maximum commission potential via transparent structures.",
                "AI-enabled platform to manage leads and apps.",
                "Access to 50,000+ global and domestic programs.",
                "Automated tracking and digitized workflows."
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500 shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      },
      {
        q: "How do I find and submit an application for a university?",
        a: "Vidyazon standardizes the process: 1. Go to the Applications section. 2. Select the student profile. 3. Choose the institution and program. The platform automatically generates the application form using the student’s data and prompts for missing requirements."
      },
      {
        q: "How does Vidyazon help me compare Indian and overseas options?",
        a: "One of Vidyazon’s goals is to show you both categories—Indian institutions and overseas options—on the same platform. You can compare fees, entry requirements, duration, and outcomes so you choose based on value rather than only on destination hype."
      }
    ],
    "Students": [
      {
        q: "What happens after I submit my application through Vidyazon?",
        a: "Vidyazon’s admissions team reviews your file for completeness and shares it with your chosen institution(s). The institution then evaluates your application and shares a decision, which Vidyazon passes on to you through your dashboard, email, or counselor."
      },
      {
        q: "When should I start my visa process?",
        a: "You should begin planning your visa a few months before your intended travel date—ideally after you receive your Acceptance Letter and meet all academic and financial conditions."
      },
      {
        q: "Can I delay paying tuition fees after receiving an offer?",
        a: "Some institutions allow deferral for valid reasons (visa delay, medical issues), while others are strict. Deferral may involve additional charges or limited seat availability, so you must check the exact policy of your chosen institution."
      }
    ],
    "Agents & Partners": [
      {
        q: "What is the Agent Vetting Process?",
        a: (
          <div className="space-y-2">
            <p>Prospective agents join through five stages:</p>
            <ol className="list-decimal pl-5 space-y-1 text-sm">
              <li>Initial Contact via regional managers or portal.</li>
              <li>Introductory Discussion on models and governance.</li>
              <li>Application and Documentation submission.</li>
              <li>Verification and Background Checks.</li>
              <li>Approval, Agreement signing, and Onboarding.</li>
            </ol>
          </div>
        )
      },
      {
        q: "How does the global/country office model (GMO) work?",
        a: "We represent partner institutions with dedicated in-country staff. We are present full-time, speaking the local language and working in local time zones. GMO partners are direct and recognized agents of Higher Education Institutions (HEIs) and are paid directly by them."
      },
      {
        q: "What are the basic requirements to become a partner?",
        a: "Typically: Valid Business Registration, photos of office premises, at least two institutional references, and agreement to our Partner Code of Conduct and Data Protection terms."
      }
    ],
    "Institutions": [
      {
        q: "How long does it take to onboard a new institution?",
        a: "Onboarding is designed to be simple. Once the representation agreement is signed and program data is provided in the required format, your basic profile can usually go live within a few business days."
      },
      {
        q: "Can Vidyazon integrate with our existing LMS or CRM?",
        a: "Yes. Integration is a core principle. Data can be securely imported/exported via APIs or structured formats. Application and enrollment info from Vidyazon can be mapped to your internal systems during the technical onboarding phase."
      },
      {
        q: "How does Vidyazon support diversified student recruitment?",
        a: "We combine an on-ground network of curated counselors across Tier 1, 2, and 3 cities with a digital platform. This multiregion presence helps institutions attract a diverse mix of learners instead of relying on a single source country."
      }
    ],
    "Governance & Legal": [
      {
        q: "What are the Core Ethical Principles for partners?",
        a: (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-bold text-blue-500 text-sm">Integrity</h4>
              <p className="text-xs">Avoid misleading claims or false promises.</p>
            </div>
            <div>
              <h4 className="font-bold text-blue-500 text-sm">Transparency</h4>
              <p className="text-xs">Disclose all service fees clearly to students.</p>
            </div>
            <div>
              <h4 className="font-bold text-blue-500 text-sm">Confidentiality</h4>
              <p className="text-xs">Protect sensitive student and institutional data.</p>
            </div>
            <div>
              <h4 className="font-bold text-blue-500 text-sm">Objectivity</h4>
              <p className="text-xs">Prioritize the learner's best interests.</p>
            </div>
          </div>
        )
      },
      {
        q: "What are the consequences of a breach in governance?",
        a: "Actions include: Formal warnings, temporary restriction of portal access, account suspension, or permanent termination of the partnership. Serious fraud is reported to the relevant institutions or authorities."
      },
      {
        q: "How are cookies used on the Vidyazon portal?",
        a: "We use cookies to remember login details, store language/location preferences, and analyze how users navigate the portal to improve performance. You can manage these via your browser settings, though some features may be limited if disabled."
      }
    ]
  }), []);

  const categories = [
    { id: 'General', icon: HelpCircle, label: 'General' },
    { id: 'Students', icon: GraduationCap, label: 'For Students' },
    { id: 'Agents & Partners', icon: Briefcase, label: 'For Agents' },
    { id: 'Institutions', icon: Building2, label: 'Institutions' },
    { id: 'Governance & Legal', icon: Scale, label: 'Legal & Policy' }
  ];

  const filteredQuestions = useMemo(() => {
    return faqData[activeCategory].filter(item => 
      item.q.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [activeCategory, searchQuery, faqData]);

  return (
    <div className={`min-h-screen ${styles.bg} transition-colors duration-500`}>
      {/* Visual Ambience */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] opacity-10 bg-blue-600`} />
      </div>

      <div className="relative z-10 pt-32 pb-20 px-4">
        
        {/* --- HEADER --- */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 border border-blue-500/20 mb-6 font-bold text-xs uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" /> Trusted Education Marketplace
          </div>
          <h1 className={`text-4xl md:text-5xl font-black mb-6 tracking-tight ${styles.text}`}>
            Knowledge <span className="text-blue-600">Base</span>
          </h1>
          <p className={`max-w-2xl mx-auto mb-10 ${styles.subText}`}>
            Find detailed answers about our partner models, recruitment governance, and student application journeys.
          </p>
          
          {/* Search Bar */}
          <div className="mt-10 max-w-xl mx-auto relative">
            <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search by keyword (e.g. Visa, Commission, Refund)..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full py-4 pl-14 pr-6 rounded-2xl border-2 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all shadow-sm ${styles.input}`}
            />
          </div>
        </div>

        {/* --- CATEGORY TABS --- */}
        <div className="max-w-5xl mx-auto mb-12 overflow-x-auto">
          <div className="flex justify-start md:justify-center gap-2 min-w-max px-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setOpenIndex(null); }}
                className={`px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 transition-all border text-sm ${activeCategory === cat.id ? styles.activeTab : styles.inactiveTab}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* --- FAQ ACCORDION --- */}
        <div className="max-w-3xl mx-auto space-y-3">
          {filteredQuestions.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div 
                key={index} 
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${styles.card} ${isOpen ? 'ring-1 ring-blue-500 border-blue-500 shadow-md' : 'hover:border-blue-300'}`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                >
                  <span className={`font-bold text-base ${isOpen ? 'text-blue-600' : styles.text}`}>
                    {item.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : 'text-gray-400'}`} />
                </button>
                
                <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className={`px-6 pb-6 text-sm leading-relaxed border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'} pt-4 ${styles.subText}`}>
                    {typeof item.a === 'string' ? <p>{item.a}</p> : item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* --- CONTACT CTA --- */}
        <div className={`max-w-3xl mx-auto mt-16 p-8 rounded-3xl border text-center ${styles.card}`}>
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8" />
          </div>
          <h3 className={`text-xl font-bold mb-2 ${styles.text}`}>Still have questions?</h3>
          <p className={`mb-6 text-sm ${styles.subText}`}>Our support team is available to help you with specific inquiries regarding your partnership or application.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="mailto:info@vidyazon.com" className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" /> Email Us
            </a>
            <a href="/contact" className={`px-6 py-3 rounded-xl font-bold text-sm transition-all border flex items-center justify-center gap-2 ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
              Contact Form <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const ArrowRight = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);